import React from "react";

import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";

const DiscoveryFooter: React.FC = () => {
	const navigate = useNavigate();
	return (
		<footer className="border-t bg-[#2A2A2A] border-gray-800 text-white">
			<div className="px-6 py-12">
				<div className="mx-auto max-w-7xl">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
						{/* Company Info */}
						<div className="lg:col-span-1">
							<div className="flex items-center mb-4">
								<Button
									variant="ghost"
									className="p-0 rounded-none hover:bg-transparent focus:bg-transparent"
									onClick={() => {
										navigate({ to: "/" });
										window.scrollTo({ top: 0, behavior: "auto" });
									}}
								>
									<img
										src="/logo-with-color.png"
										className="w-48"
										alt="Platform Logo"
									/>
								</Button>
							</div>
							<p className="text-gray-400 text-sm leading-relaxed mb-4">
								Intasida is Nigeria's trusted e-commerce escrow platform connecting businesses with verified vendors for secure and
								reliable transactions. We safeguard your payments until you receive exactly what you ordered.
							</p>
							<div className="flex space-x-4 mt-4">
								<a href="https://www.instagram.com/official_intasida?igsh=M3c1cjE1c29mbTc5" target="_blank" rel="noreferrer noopener" className="text-gray-400 hover:text-white transition-colors">
									<svg className="size-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
										<rect width="20" height="20" x="2" y="2" rx="5" />
										<path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37Z" />
										<line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
									</svg>
								</a>
								<a href="#" className="text-gray-400 hover:text-white transition-colors">
									<svg className="size-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a4 4 0 0 0-4 4v3H6v4h5v8h4v-8h3l1-4h-4V6a1 1 0 0 1 1-1h3z" /></svg>
								</a>
								<a href="https://x.com/official_intasida" target="_blank" rel="noreferrer noopener" className="text-gray-400 hover:text-white transition-colors">
									<svg className="size-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53A4.48 4.48 0 0 0 22.43.36a9.09 9.09 0 0 1-2.88 1.1A4.52 4.52 0 0 0 16.11 0c-2.5 0-4.52 2.02-4.52 4.52 0 .35.04.7.11 1.03C7.69 5.4 4.07 3.6 1.64.96c-.38.65-.6 1.4-.6 2.2 0 1.52.77 2.86 1.94 3.64A4.48 4.48 0 0 1 .96 6v.06c0 2.13 1.52 3.91 3.54 4.31-.37.1-.76.16-1.16.16-.28 0-.55-.03-.81-.08.55 1.72 2.16 2.97 4.07 3A9.05 9.05 0 0 1 0 19.54a12.8 12.8 0 0 0 6.92 2.03c8.3 0 12.84-6.87 12.84-12.84 0-.2 0-.39-.01-.58A9.22 9.22 0 0 0 24 4.59a9.1 9.1 0 0 1-2.6.71A4.52 4.52 0 0 0 23 3Z" /></svg>
								</a>
								<a href="https://www.tiktok.com/@intasida_official?_r=1&_t=ZS-94a0TknvQn7" target="_blank" rel="noreferrer noopener" className="text-gray-400 hover:text-white transition-colors">
									<svg className="size-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.19 8.19 0 0 0 4.76 1.52V6.79a4.83 4.83 0 0 1-1-.1Z" /></svg>
								</a>
							</div>
						</div>

						{/* Quick Links */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Quick Links</h3>
							<ul className="space-y-3">
								<li>
									<Button asChild variant="link" className="text-gray-400 hover:text-white transition-colors p-0 h-auto">
										<a href="/">Home</a>
									</Button>
								</li>
								<li>
									<Button asChild variant="link" className="text-gray-400 hover:text-white transition-colors p-0 h-auto">
										<a href="/about">About Us</a>
									</Button>
								</li>
								<li>
									<Button asChild variant="link" className="text-gray-400 hover:text-white transition-colors p-0 h-auto">
										<a href="/features">Features</a>
									</Button>
								</li>
								<li>
									<Button asChild variant="link" className="text-gray-400 hover:text-white transition-colors p-0 h-auto">
										<a href="/contact">Contact Us</a>
									</Button>
								</li>
							</ul>
						</div>

						{/* Services */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Services</h3>
							<ul className="space-y-3 text-gray-400 text-sm">
								<li>Secure Escrow Payments</li>
								<li>Vendor Verification</li>
								<li>Order Tracking</li>
								<li>Dispute Resolution</li>
								<li>Integrated Logistics</li>
							</ul>
						</div>

						{/* Legal */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Legal</h3>
							<ul className="space-y-3">
								<li>
									<Button asChild variant="link" className="text-gray-400 hover:text-white transition-colors p-0 h-auto">
										<a href="/privacy">Privacy Policy</a>
									</Button>
								</li>
								<li>
									<Button asChild variant="link" className="text-gray-400 hover:text-white transition-colors p-0 h-auto">
										<a href="/terms">Terms of Service</a>
									</Button>
								</li>
								<li>
									<Button asChild variant="link" className="text-gray-400 hover:text-white transition-colors p-0 h-auto">
										<a href="/legal">Legal Information</a>
									</Button>
								</li>
							</ul>
						</div>

						{/* Contact Us */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Contact Us</h3>
							<div className="space-y-3 text-gray-400">
								<p className="flex items-center gap-2 text-sm">
									<svg className="size-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
									info@intasida.com
								</p>
								<p className="flex items-center gap-2 text-sm">
									<svg className="size-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
									markanthony@intasida.com
								</p>
								<p className="flex items-center gap-2 text-sm">
									<svg className="size-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
									+234 814 222 4513
								</p>
								<p className="flex items-start gap-2 text-sm">
									<svg className="size-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
										<path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
										<path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
									</svg>
									12 Mrs. Pelizer Ogar street, Independence layout, Enugu state.
								</p>
							</div>
						</div>
					</div>

					{/* Download Section */}
					<div className="mt-12 pt-8 border-t border-gray-800">
						<div className="flex flex-col md:flex-row items-center justify-between gap-6">
							<div>
								<h3 className="text-lg font-semibold mb-2">Download Intasida</h3>
								<p className="text-gray-400 text-sm">Get the app on your mobile device for a seamless experience.</p>
							</div>
							<div className="flex gap-4 items-center">
								<a
									href="https://play.google.com/store/apps/details?id=com.intasida&pcampaignid=web_share"
									target="_blank"
									rel="noreferrer noopener"
									className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg px-4 py-2 text-sm"
								>
									<svg className="size-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 01-.609-.92V2.734a.996.996 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 1.33-2.778 1.61-2.302-2.302 2.778-2.778v2.14zM5.864 2.658l10.937 6.333-2.302 2.302-8.635-8.635z" /></svg>
									Google Play
								</a>
								<a
									href="https://apps.apple.com/us/app/intasida/id6740424971"
									target="_blank"
									rel="noreferrer noopener"
									className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg px-4 py-2 text-sm"
								>
									<svg className="size-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
									App Store
								</a>
							</div>
						</div>
					</div>

					{/* Bottom Section */}
					<div className="flex flex-col md:flex-row justify-between items-center mt-8 pt-8 border-t border-gray-800">
						<p className="text-gray-400 text-sm mb-4 md:mb-0">
							© 2026 Intasida Limited. All rights reserved.
						</p>
						<div className="flex flex-wrap gap-4 text-gray-400 text-sm">
							<a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
							<a href="/terms" className="hover:text-white transition-colors">Terms</a>
							<a href="/contact" className="hover:text-white transition-colors">Support</a>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default DiscoveryFooter;
